'use client';
import * as React from 'react';
import { useTranslations } from 'next-intl';
import { Input } from '@/components/ui/field';
import { verifyPhoneSignup } from '@/lib/actions';
import { Button, FormError } from './ui';

/** Finish a phone-only signup with the LuxMed-owned challenge. The API consumes the OTP,
 * creates the confirmed Supabase user and releases the password session atomically. */
export function PhoneSignupVerification({
  challengeId,
  password,
  next,
}: {
  challengeId: string;
  password: string;
  next: string;
}) {
  const t = useTranslations('auth');
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [pending, startTransition] = React.useTransition();

  const run = (operation: () => Promise<void>) => {
    setError(null);
    startTransition(async () => {
      try {
        await operation();
      } catch {
        setError(t('phoneActivationError'));
      }
    });
  };

  const verify = () =>
    run(async () => {
      const result = await verifyPhoneSignup(challengeId, code, password);
      if (!result.ok) throw new Error();
      window.location.assign(next || '/app');
    });

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        verify();
      }}
    >
      <p className="text-sm text-muted-foreground">{t('phoneSignupCodeBody')}</p>
      <Input
        value={code}
        onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 4))}
        inputMode="numeric"
        autoComplete="one-time-code"
        icon="shield-key"
        placeholder={t('mfaCodePlaceholder')}
        required
      />
      <FormError>{error}</FormError>
      <Button type="submit" block size="lg" loading={pending}>
        {t('mfaConfirm')}
      </Button>
    </form>
  );
}
